import {
	Box,
	Card,
	CardContent,
	Chip,
	Grid,
	Stack,
	Typography,
} from "@mui/material";
import { safeBoolean } from "../../utils/CommonFunctions";
import {
	MyBusinessIcon,
	MyCancelIcon,
	MyCheckCircleIcon,
	MyCheckIcon,
	MyCreditCardIcon,
	MyEmailIcon,
	MyLanguageIcon,
	MyPersonIcon,
	MyPhoneIcon,
	MyVerifiedIcon,
	MyCalendarIcon,
	MyLocationIcon,
	MyMapIcon,
	MyAccountBalanceWalletIcon,
	MyVisibilityIcon,
	MyVisibilityOffIcon,
} from "../../Assets/svg/MyIcons";

const TenantCreationPreview = ({
	previewData,
	planMapPrice,
	selectedCurrency,
	tenantCurrencies,
	merchantDetails,
	isPlanChanged = false,
}) => {
	const selectedPlan = planMapPrice[previewData?.planId] || {};
	const currentPlanDetails = selectedPlan;
	const currentAmount =
		previewData?.billingCycle === "monthly"
			? selectedPlan?.monthly
			: selectedPlan.yearly;
	const currencyDetails = tenantCurrencies.find(
		(c) => c.id === Number(selectedCurrency),
	) || { exchangeRate: "1.0000", symbol: "CI$" };

	let noteText = "";
	const originalPlan = merchantDetails?.planId
		? planMapPrice[merchantDetails.planId]
		: null;
	const nextPlanPrice = previewData?.billingCycle === "yearly"
		? selectedPlan?.yearly
		: selectedPlan?.monthly;
	const currentPlanPrice = originalPlan
		? previewData?.billingCycle === "yearly"
			? originalPlan.yearly
			: originalPlan.monthly
		: null;
	const isUpgradeChange =
		isPlanChanged &&
		currentPlanPrice != null &&
		nextPlanPrice != null &&
		Number(nextPlanPrice) >= Number(currentPlanPrice);
	const isDowngradeChange =
		isPlanChanged &&
		currentPlanPrice != null &&
		nextPlanPrice != null &&
		Number(nextPlanPrice) < Number(currentPlanPrice);

	if (isUpgradeChange) {
		noteText =
			previewData.paymentMode === "online"
				? "Plan upgrade starts today. Unused credits and wallet balance are applied first. If any amount remains, a payment link will be emailed to the tenant."
				: "Plan upgrade starts today. Unused credits and wallet balance are applied first. Offline payment is recorded by admin.";
	} else if (isDowngradeChange) {
		noteText =
			previewData.paymentMode === "online"
				? "Plan downgrade will apply from the next billing cycle. Wallet is used first; remaining amount is collected via payment link emailed to the tenant."
				: "Plan downgrade will apply from the next billing cycle. Wallet is used first for any scheduled charge.";
	} else if (
		previewData.planHistory &&
		previewData?.planHistory[0]?.paymentMode !== previewData.paymentMode &&
		previewData.paymentMode === "online"
	) {
		noteText =
			"The payment mode will be changed to Online. A payment link will be sent to the tenant’s email two days before the current plan expires.";
	} else if (
		previewData.planHistory &&
		previewData.planHistory[0]?.paymentMode !== previewData.paymentMode &&
		previewData.paymentMode === "offline"
	) {
		noteText =
			"The payment mode will be changed to Offline. The admin must manually verify the payment after it is received from the tenant.";
	} else if (
		previewData.planHistory &&
		previewData.planHistory[0].paymentMode === previewData.paymentMode &&
		previewData.isAutoPay
	) {
		if (
			previewData.planHistory &&
			previewData.planHistory[0].paymentMode === "online"
		) {
			noteText =
				"Subscription is enabled for this tenant. The payment will be automatically deducted on the due date.";
		} else {
			noteText =
				"Subscription is enabled for this tenant. The admin must manually verify the payment once it is received from the tenant on the due date.";
		}
	} else {
		noteText =
			"Subscription is disabled for this tenant. The admin must manually process the payment on the due date.";
	}

	const hasActiveDowngradeRequest = Boolean(
		previewData?.isDowngradeRequested || merchantDetails?.isDowngradeRequested,
	);

	const getFeatureValue = (item) => {
		const featureName = (item?.feature || "").toLowerCase();
		let value;

		// For custom plans, check custom limits and handle null as "Unlimited"
		if (previewData?.isCustomPlan && previewData?.customPlanDetails) {
			if (
				featureName.includes("user") &&
				previewData.customPlanDetails.userLimit !== undefined
			) {
				value =
					previewData.customPlanDetails.userLimit === null
						? "Unlimited"
						: previewData.customPlanDetails.userLimit;
			} else if (
				featureName.includes("message") &&
				previewData.customPlanDetails.messageLimit !== undefined
			) {
				value =
					previewData.customPlanDetails.messageLimit === null
						? "Unlimited"
						: previewData.customPlanDetails.messageLimit;
			} else if (
				featureName.includes("agent") &&
				previewData.customPlanDetails.agentLimit !== undefined
			) {
				value =
					previewData.customPlanDetails.agentLimit === null
						? "Unlimited"
						: previewData.customPlanDetails.agentLimit;
			} else {
				value =
					item?.value === null ? "Unlimited" : (item?.value ?? "");
			}
		} else {
			// Return the plan's default feature value (handle null as "Unlimited" too)
			value = item?.value === null ? "Unlimited" : (item?.value ?? "");
		}

		// Capitalize "unlimited" if it comes from backend as lowercase
		if (typeof value === "string" && value.toLowerCase() === "unlimited") {
			return "Unlimited";
		}
		return value;
	};

	const InfoRow = ({ label, value, icon }) => (
		<Box sx={{ py: 1 }}>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
				{label}
			</Typography>
			<Stack direction="row" spacing={1} alignItems="center">
				{icon && (
					<Box
						sx={{
							color: "var(--bs-primary)",
							display: "flex",
							minWidth: 20,
						}}
					>
						{icon}
					</Box>
				)}
				<Typography variant="body1" sx={{ fontWeight: 600 }}>
					{value || "-"}
				</Typography>
			</Stack>
		</Box>
	);

	return (
		<Grid container spacing={2}>
			{/* Tenant Information Card */}
			<Grid item xs={12} md={8}>
				<Card
					sx={{
						height: "100%",
						boxShadow: "0 20px 60px rgba(0,0,0,0.1)",
					}}
				>
					<CardContent sx={{ p: 3 }}>
						<Stack
							direction="row"
							alignItems="center"
							spacing={1.5}
							sx={{ mb: 2 }}
						>
							<Box
								sx={{
									bgcolor: "var(--bs-primary)",
									p: 1,
									borderRadius: 1,
									display: "flex",
								}}
							>
								<MyPersonIcon
									sx={{ fontSize: 24, color: "white" }}
								/>
							</Box>
							<Typography variant="h6" fontWeight={600}>
								Tenant Information
							</Typography>
						</Stack>

						{/* Personal Details */}
						<Box
							sx={{
								bgcolor: "grey.50",
								p: 2.5,
								borderRadius: 1,
								mb: 3,
								boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
							}}
						>
							<Typography
								variant="subtitle1"
								fontWeight={600}
								color="var(--bs-primary)"
								sx={{ mb: 1.5 }}
							>
								Personal Details
							</Typography>
							<Grid container spacing={2}>
								{[
									{
										label: "First Name",
										value: previewData.firstName,
										icon: <MyPersonIcon fontSize="small" />,
										cols: 6,
									},
									{
										label: "Last Name",
										value: previewData.lastName,
										icon: <MyPersonIcon fontSize="small" />,
										cols: 6,
									},
									{
										label: "Email",
										value: previewData.email,
										icon: <MyEmailIcon fontSize="small" />,
										cols: 12,
									},
								].map((item, index) => (
									<Grid
										item
										xs={12}
										sm={item.cols}
										key={index}
									>
										<InfoRow
											label={item.label}
											value={item.value}
											icon={item.icon}
										/>
									</Grid>
								))}
							</Grid>
						</Box>

						{/* Company Details */}
						<Box
							sx={{
								bgcolor: "grey.50",
								p: 2.5,
								borderRadius: 1,
								mb: 3,
								boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
							}}
						>
							<Typography
								variant="subtitle1"
								fontWeight={600}
								color="var(--bs-primary)"
								sx={{ mb: 1.5 }}
							>
								Company Details
							</Typography>
							<Grid container spacing={2}>
								{[
									{
										label: "Company Name",
										value: previewData.companyName,
										icon: (
											<MyBusinessIcon fontSize="small" />
										),
										cols: 6,
									},
									{
										label: "Company Email",
										value: previewData.companyEmail,
										icon: <MyEmailIcon fontSize="small" />,
										cols: 6,
									},
									{
										label: "Domain URL",
										value: previewData.domainUrl,
										icon: (
											<MyLanguageIcon fontSize="small" />
										),
										cols: 6,
									},
									{
										label: "Phone Number",
										value: previewData.companyPhoneNumber,
										icon: <MyPhoneIcon fontSize="small" />,
										cols: 6,
									},
									{
										label: "Address",
										value: previewData.companyAddress,
										icon: (
											<MyBusinessIcon fontSize="small" />
										),
										cols: 12,
									},
									{
										label: "Zip Code",
										value: previewData.companyZipCode,
										icon: (
											<MyLocationIcon fontSize="small" />
										),
										cols: 4,
									},
									{
										label: "Country",
										value: previewData.companyCountry,
										icon: (
											<MyLanguageIcon fontSize="small" />
										),
										cols: 4,
									},
									{
										label: "State",
										value: previewData.companyState,
										icon: <MyMapIcon fontSize="small" />,
										cols: 4,
									},
								].map((item, index) => (
									<Grid
										item
										xs={12}
										sm={item.cols}
										key={index}
									>
										<InfoRow
											label={item.label}
											value={item.value}
											icon={item.icon}
										/>
									</Grid>
								))}
							</Grid>
						</Box>

						{/* Tenant Configuration */}
						<Box
							sx={{
								bgcolor: "grey.50",
								p: 2.5,
								borderRadius: 1,
								mb: 3,
								boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
							}}
						>
							<Typography
								variant="subtitle1"
								fontWeight={600}
								color="var(--bs-primary)"
								sx={{ mb: 1.5 }}
							>
								Tenant Configuration
							</Typography>
							<Grid container spacing={2}>
								<Grid item xs={12} sm={4}>
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{ mb: 0.5 }}
									>
										Domain Verification
									</Typography>
									<Chip
										icon={
											previewData.isVerified ? (
												<MyVerifiedIcon />
											) : (
												<MyCancelIcon />
											)
										}
										label={
											previewData.isVerified
												? "Verified"
												: "Unverified"
										}
										color={
											previewData.isVerified
												? "success"
												: "error"
										}
										size="medium"
									/>
								</Grid>
								<Grid item xs={12} sm={4}>
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{ mb: 0.5 }}
									>
										Subscription Status
									</Typography>
									<Chip
										icon={
											safeBoolean(
												previewData?.isAutoPay,
											) ? (
												<MyCheckCircleIcon className="text-success" />
											) : (
												<MyCancelIcon className="text-danger" />
											)
										}
										label={
											safeBoolean(previewData?.isAutoPay)
												? "Enabled"
												: "Disabled"
										}
										className={
											safeBoolean(previewData?.isAutoPay)
												? "text-success"
												: "text-danger"
										}
										sx={{ fontWeight: 600 }}
									/>
								</Grid>
								<Grid item xs={12} sm={4}>
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{ mb: 0.5 }}
									>
										Currency
									</Typography>
									<Chip
										icon={
											<Typography
												component="span"
												sx={{
													fontSize: "1.4rem",
													fontWeight: 600,
													lineHeight: 1,
												}}
											>
												{currencyDetails?.symbol}
											</Typography>
										}
										label={
											currencyDetails?.name ||
											currencyDetails?.currencyName ||
											""
										}
										size="medium"
										sx={{
											backgroundColor:
												"var(--bs-primary)",
											color: "white",
											"& .MuiChip-icon": {
												color: "white",
												marginLeft: "10px",
											},
											"& .MuiChip-label": {
												px: 2,
											},
										}}
									/>
								</Grid>

								{previewData?.paymentMode === "offline" && (
									<Grid item xs={12} sm={12}>
										<Typography
											variant="body2"
											color="text.secondary"
											sx={{ mb: 0.5 }}
										>
											Billing Details Visibility
										</Typography>
										<Chip
											icon={
												previewData?.hideBillingDetails ? (
													<MyVisibilityOffIcon />
												) : (
													<MyVisibilityIcon />
												)
											}
											label={
												previewData?.hideBillingDetails
													? "Hidden - Tenant cannot view billing details"
													: "Visible - Tenant can view billing details"
											}
											size="medium"
											sx={{
												fontWeight: 600,
												minWidth: "300px",
												justifyContent: "flex-start",
											}}
										/>
									</Grid>
								)}

								<Grid item xs={12} sm={12}>
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{ mb: 0.5 }}
									>
										AI Providers Visibility
									</Typography>
									<Chip
										icon={
											previewData?.hideAiProviders ? (
												<MyVisibilityOffIcon />
											) : (
												<MyVisibilityIcon />
											)
										}
										label={
											previewData?.hideAiProviders
												? "Hidden - Tenant cannot manage AI Providers (default provider used)"
												: "Visible - Tenant can manage AI Providers"
										}
										size="medium"
										sx={{
											fontWeight: 600,
											minWidth: "300px",
											justifyContent: "flex-start",
										}}
									/>
								</Grid>
							</Grid>
						</Box>

						{/* Allowed Features */}
						<Box
							sx={{
								bgcolor: "grey.50",
								p: 2.5,
								borderRadius: 1,
								mt: 3,
								boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
							}}
						>
							<Typography
								variant="subtitle1"
								fontWeight={600}
								color="var(--bs-primary)"
								sx={{ mb: 1.5 }}
							>
								Allowed Features
							</Typography>
							<Grid container spacing={2}>
								{[
									{ label: "User Chat", key: "user_chat" },
									{ label: "AI Chat", key: "ai_chat" },
									{
										label: "Ticket v1",
										key: "support_tickets",
									},
									{
										label: "Ticket v2",
										key: "ticket_v2",
									},
									{
										label: "Web Mail Tickets",
										key: "web_mail_tickets",
									},
								].map((feature) => {
									const isEnabled = safeBoolean(
										previewData?.allowedFeatures?.[
											feature.key
										],
									);
									return (
										<Grid item xs={3} key={feature.key}>
											<Box sx={{ py: 1 }}>
												<Typography
													variant="body2"
													color="text.secondary"
													sx={{ mb: 0.5 }}
												>
													{feature.label}
												</Typography>
												<Stack
													direction="row"
													spacing={1}
													alignItems="center"
												>
													{isEnabled ? (
														<MyCheckCircleIcon
															fontSize="small"
															className="text-success"
														/>
													) : (
														<MyCancelIcon
															fontSize="small"
															className="text-danger"
														/>
													)}
													<Typography
														variant="body1"
														fontWeight={600}
													>
														{isEnabled
															? "Enabled"
															: "Disabled"}
													</Typography>
												</Stack>
											</Box>
										</Grid>
									);
								})}
							</Grid>
						</Box>
					</CardContent>
				</Card>
			</Grid>

			{/* Plan Information Card */}
			<Grid item xs={12} md={4}>
				<Card
					sx={{
						height: "100%",
						background:
							"linear-gradient(135deg, #466bb3 0%, #22419e 100%)",
						boxShadow: "0 20px 60px rgba(34, 65, 158, 0.3)",
					}}
				>
					<CardContent sx={{ p: 3 }}>
						<Stack
							direction="row"
							alignItems="center"
							spacing={1.5}
							sx={{ mb: 2 }}
						>
							<Box
								sx={{
									bgcolor: "white",
									p: 1,
									borderRadius: 1,
									display: "flex",
								}}
							>
								<MyCreditCardIcon
									sx={{
										fontSize: 24,
										color: "var(--bs-primary)",
									}}
								/>
							</Box>
							<Typography
								variant="h6"
								fontWeight={600}
								sx={{ color: "white" }}
							>
								Plan Information
							</Typography>
						</Stack>

						{/* Plan Details */}
						<Box
							sx={{
								bgcolor: "rgba(255,255,255,0.15)",
								p: 2,
								borderRadius: 1,
								mb: 2,
								backdropFilter: "blur(10px)",
							}}
						>
							<Typography
								variant="subtitle1"
								fontWeight={600}
								sx={{ mb: 1.5, color: "white" }}
							>
								Plan Details
								{isDowngradeChange
									? " (upcoming)"
									: isUpgradeChange
										? " (effective today)"
										: isPlanChanged
											? " (upcoming)"
											: ""}
							</Typography>
							<Stack spacing={1.5}>
								{[
									{
										label: "Plan Name",
										value: previewData?.planId ? `${previewData?.isCustomPlan ? "Custom" : `${selectedPlan?.name || "-"}`}` : "-",
										icon: (
											<MyCreditCardIcon fontSize="small" />
										),
										variant: "body1",
									},
									{
										label: "Plan Price",
										value: previewData?.planId
											? `${currencyDetails?.symbol}${
													previewData?.isCustomPlan &&
													previewData.customPlanDetails?.price !== null &&
													previewData
														.customPlanDetails
														?.price !== undefined
														? Number(previewData.customPlanDetails.price).toFixed(2)
														: currentAmount
															? Number(
																	Number(currentAmount) * Number(currencyDetails?.exchangeRate),
																).toFixed(2)
															: "0.00"
												}`
											: "-",
										icon: (
											<MyAccountBalanceWalletIcon fontSize="small" />
										),
										variant: "h5",
									},
								].map((item, index) => (
									<Box sx={{ py: 1 }} key={index}>
										<Typography
											variant="body2"
											sx={{
												color: "rgba(255,255,255,0.8)",
												display: "block",
												mb: 0.5,
											}}
										>
											{item.label}
										</Typography>
										<Stack
											direction="row"
											spacing={1}
											alignItems="center"
										>
											<Box
												sx={{
													color: "white",
													display: "flex",
													minWidth: 20,
												}}
											>
												{item.icon}
											</Box>
											<Typography
												variant={item.variant}
												fontWeight={item.variant === "h5" ? 700 : 600}
												sx={{ color: "white" }}
											>
												{item.value}
											</Typography>
										</Stack>
									</Box>
								))}
							</Stack>
						</Box>

						{/* Billing Information */}
						<Box
							sx={{
								bgcolor: "rgba(255,255,255,0.15)",
								p: 2,
								borderRadius: 1,
								mb: 2,
								backdropFilter: "blur(10px)",
							}}
						>
							<Typography
								variant="subtitle1"
								fontWeight={600}
								sx={{ mb: 1.5, color: "white" }}
							>
								Billing Information
								{isDowngradeChange
									? " (upcoming)"
									: isUpgradeChange
										? " (effective today)"
										: isPlanChanged
											? " (upcoming)"
											: ""}
							</Typography>
							<Stack spacing={1.5}>
								{[
									{
										label: "Payment Mode",
										value: previewData?.paymentMode || "-",
										icon: (
											<MyCreditCardIcon fontSize="small" />
										),
									},
									{
										label: "Billing Cycle",
										value: previewData?.billingCycle || "-",
										icon: (
											<MyCalendarIcon fontSize="small" />
										),
									},
								].map((item, index) => (
									<Box sx={{ py: 1 }} key={index}>
										<Typography
											variant="body2"
											sx={{
												color: "rgba(255,255,255,0.8)",
												display: "block",
												mb: 0.5,
											}}
										>
											{item.label}
										</Typography>
										<Stack
											direction="row"
											spacing={1}
											alignItems="center"
										>
											<Box
												sx={{
													color: "white",
													display: "flex",
													minWidth: 20,
												}}
											>
												{item.icon}
											</Box>
											<Typography
												variant="body1"
												fontWeight={600}
												sx={{
													textTransform: "capitalize",
													color: "white",
												}}
											>
												{item.value}
											</Typography>
										</Stack>
									</Box>
								))}
							</Stack>
						</Box>

						{/* Plan Features Box */}
						{currentPlanDetails?.features && (
							<Box
								sx={{
									bgcolor: "rgba(255,255,255,0.15)",
									p: 2,
									borderRadius: 1,
									backdropFilter: "blur(10px)",
								}}
							>
								<Typography
									variant="subtitle1"
									fontWeight={600}
									sx={{ mb: 1.5, color: "white" }}
								>
									Plan Features
									{isDowngradeChange
										? " (upcoming)"
										: isUpgradeChange
											? " (effective today)"
											: isPlanChanged
												? " (upcoming)"
												: ""}
								</Typography>
								<Stack spacing={1.5}>
									{currentPlanDetails?.features?.map(
										(item, index) => {
											const value = getFeatureValue(item);

											const featureName = (
												item?.feature || ""
											).toLowerCase();
											// Determine which icon to use based on feature name
											let FeatureIcon = MyCheckIcon;
											if (featureName.includes("user")) {
												FeatureIcon = MyPersonIcon;
											} else if (
												featureName.includes("message")
											) {
												FeatureIcon = MyEmailIcon;
											} else if (
												featureName.includes("agent")
											) {
												FeatureIcon = MyCheckCircleIcon;
											}

											return (
												<Box key={index} sx={{ py: 1 }}>
													<Typography
														variant="body2"
														sx={{
															color: "rgba(255,255,255,0.8)",
															mb: 0.5,
														}}
													>
														{item.feature}
													</Typography>
													<Stack
														direction="row"
														spacing={1}
														alignItems="center"
													>
														<FeatureIcon fontSize="small" />
														<Typography
															variant="body1"
															fontWeight={600}
															sx={{
																color: "white",
															}}
														>
															{value || "-"}
														</Typography>
													</Stack>
												</Box>
											);
										},
									)}
								</Stack>
							</Box>
						)}

						{/* Note Section */}
						{previewData?.id && (
							<Box
								sx={{
									bgcolor: "rgba(255,255,255,0.15)",
									p: 2,
									borderRadius: 1,
									mt: 2,
									backdropFilter: "blur(10px)",
								}}
							>
								<Typography
									variant="subtitle1"
									fontWeight={600}
									sx={{ mb: 0, color: "white" }}
								>
									Note
								</Typography>
								<Stack spacing={1.5}>
									<Box sx={{ py: 1 }}>
										<Typography
											variant="body2"
											className="display-block text-white"
										>
											{noteText}
										</Typography>
										{hasActiveDowngradeRequest && isPlanChanged && (
											<Typography
												variant="body2"
												className="display-block text-white"
												sx={{ mt: 1.5 }}
											>
												If there is an existing active downgrade
												request, it will be automatically cancelled
												when you update the plan.
											</Typography>
										)}
									</Box>
								</Stack>
							</Box>
						)}
					</CardContent>
				</Card>
			</Grid>
		</Grid>
	);
};

export default TenantCreationPreview;
